/**
 * Notices when nobody is working the frequency: no key, click or wheel for `minutes`, or the
 * tab hidden. A game left running in a background tab keeps asking the model for pilot calls
 * and keeps a paid radio session open; pausing it costs a player nothing but one key press.
 * No DOM beyond the event target it listens on, so the timing is testable with fake timers.
 */
export type IdleReason = 'idle' | 'hidden';

export interface IdleWatchOptions {
  /** Zero or less turns the inactivity timer off; a hidden tab still pauses. */
  minutes: number;
  onIdle(reason: IdleReason): void;
  target?: Pick<Document, 'addEventListener' | 'removeEventListener' | 'visibilityState'>;
  now?: () => number;
  /** How often the clock is checked. Background tabs throttle timers, which the hidden check covers. */
  checkMs?: number;
}

const ACTIVITY = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;

export function createIdleWatch(options: IdleWatchOptions): { touch(): void; dispose(): void } {
  const target = options.target ?? document, now = options.now ?? (() => Date.now());
  const limitMs = Math.max(0, options.minutes) * 60000;
  let last = now();
  const touch = () => { last = now(); };
  const visibility = () => { if (target.visibilityState === 'hidden') options.onIdle('hidden'); };
  for (const type of ACTIVITY) target.addEventListener(type, touch, { capture: true, passive: true });
  target.addEventListener('visibilitychange', visibility);
  const timer = limitMs > 0 ? setInterval(() => { if (now() - last >= limitMs) { last = now(); options.onIdle('idle'); } }, options.checkMs ?? 5000) : null;
  return {
    touch,
    dispose() {
      if (timer) clearInterval(timer);
      for (const type of ACTIVITY) target.removeEventListener(type, touch, { capture: true });
      target.removeEventListener('visibilitychange', visibility);
    },
  };
}
